import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import logocolegio from "../img/logocolegio.png";
import logoEnfernet from "../img/logoENFERNETlogin.png";

const API_BASE =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.trim()) ||
  (typeof window !== "undefined"
    ? window.location.origin.replace("app.", "api.")
    : "");

function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState(null);

  const [openReset, setOpenReset] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetMensaje, setResetMensaje] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  const navigate = useNavigate();
  const { login, usuario } = useAuth();

  useEffect(() => {
    if (usuario) {
      navigate("/dashboard");
    }
  }, [usuario, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const abrirRecuperacion = () => {
    setResetEmail(form.email || "");
    setResetMensaje("");
    setResetError("");
    setOpenReset(true);
  };

  const handleEnviarRecuperacion = async () => {
    setResetMensaje("");
    setResetError("");

    if (!resetEmail.trim()) {
      setResetError("Introduce tu email.");
      return;
    }

    try {
      setResetLoading(true);

      await fetch(`${API_BASE}/colegiado/reset-password-request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: resetEmail.trim() }),
      });

      setResetMensaje(
        "Si el correo existe en nuestra base de datos, se enviará una nueva contraseña a este email."
      );
    } catch (error) {
      console.error(error);
      setResetError("Error al enviar la solicitud. Inténtalo de nuevo.");
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("application/json")) {
        throw new Error("Respuesta no-JSON del servidor");
      }

      const data = await res.json();

      if (data.token) {
        const payload = JSON.parse(atob(data.token.split(".")[1]));
        const roles = payload.authorities || [];

        const rol = roles.includes("ROLE_ADMIN")
          ? "ADMIN"
          : roles.includes("ROLE_PENDIENTE")
          ? "PENDIENTE"
          : "COLEGIADO";

        if (rol === "PENDIENTE") {
          setError(
            "Su perfil está siendo verificado. Le avisaremos por email cuando pueda acceder."
          );
          return;
        }

        login(data.token);
      } else {
        setError("Credenciales incorrectas.");
      }
    } catch (err) {
      console.error("Error al conectar con el servidor:", err);
      setError("Error del servidor o conexión.");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#f0f2f5ff",
        p: 2,
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 3,
              mb: 3,
            }}
          >
            <img
              src={logocolegio}
              alt="Logo Colegio"
              style={{ width: 190, height: "auto" }}
            />
            <img
              src={logoEnfernet}
              alt="Logo EnferNET"
              style={{ width: 115, height: "auto" }}
            />
          </Box>

          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              Iniciar sesión
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Acceso al portal EnferNET
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              label="Email"
              name="email"
              type="email"
              fullWidth
              margin="normal"
              value={form.email}
              onChange={handleChange}
              required
            />

            <TextField
              label="Contraseña"
              name="password"
              type="password"
              fullWidth
              margin="normal"
              value={form.password}
              onChange={handleChange}
              required
            />

            <Box sx={{ textAlign: "right", mt: 1 }}>
              <Button
                variant="text"
                size="small"
                onClick={abrirRecuperacion}
                sx={{ textTransform: "none" }}
              >
                ¿Olvidaste la contraseña?
              </Button>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2.5, py: 1.4, fontWeight: 700 }}
            >
              Iniciar sesión
            </Button>

            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 2, py: 1.2, textTransform: "none" }}
              onClick={() => navigate("/alta-nuevo-ingreso")}
            >
              ¿No estás registrado? Solicita el alta colegial
            </Button>
          </Box>
        </Paper>
      </Container>

      <Dialog
        open={openReset}
        onClose={() => !resetLoading && setOpenReset(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Recuperar contraseña</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Introduce tu email. Si existe en nuestra base de datos, recibirás
            una nueva contraseña temporal.
          </Typography>

          {resetMensaje && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {resetMensaje}
            </Alert>
          )}

          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            disabled={resetLoading}
            autoFocus
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenReset(false)}
            disabled={resetLoading}
            sx={{ textTransform: "none" }}
          >
            Cerrar
          </Button>

          <Button
            variant="contained"
            onClick={handleEnviarRecuperacion}
            disabled={resetLoading}
            sx={{ textTransform: "none" }}
          >
            {resetLoading ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                Enviando...
              </>
            ) : (
              "Enviar nueva contraseña"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;